import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { periodosServicioAPI } from '../services/api';
import { Modal, Button, Input, Card, Badge, Tooltip, useConfirm } from './ui';

/**
 * Panel administrativo de Periodos de Servicio.
 * Catalogo maestro independiente (mensual, anual, unico, etc). Solo informativo
 * por ahora — se referencia opcionalmente desde cotizacion_servicios.periodo_id.
 * Todas las mutaciones pasan por modales para evitar cambios accidentales.
 */

const emptyForm = { nombre: '', descripcion: '', meses: '', activo: true, orden: 0 };

const PeriodosPanel = () => {
    const confirm = useConfirm();
    const [periodos, setPeriodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formModal, setFormModal] = useState({ open: false, editing: null, data: emptyForm });
    const [saving, setSaving] = useState(false);

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await periodosServicioAPI.getAll();
            const normalizados = (res.data || []).map(p => ({ ...p, activo: !!p.activo }));
            setPeriodos(normalizados);
        } catch (err) {
            toast.error('No pudimos cargar los periodos');
        } finally {
            setLoading(false);
        }
    };

    const abrirCrear = () => {
        setFormModal({ open: true, editing: null, data: { ...emptyForm, orden: periodos.length } });
    };
    const abrirEditar = (p) => {
        setFormModal({
            open: true,
            editing: p.id,
            data: {
                nombre: p.nombre,
                descripcion: p.descripcion || '',
                meses: p.meses != null ? String(p.meses) : '',
                activo: !!p.activo,
                orden: p.orden
            }
        });
    };
    const cerrarForm = () => setFormModal({ open: false, editing: null, data: emptyForm });

    const handleGuardar = async () => {
        const { editing, data } = formModal;
        if (!data.nombre.trim()) return toast.error('El nombre es obligatorio');
        setSaving(true);
        try {
            const payload = {
                nombre: data.nombre.trim(),
                descripcion: data.descripcion.trim() || null,
                meses: data.meses !== '' && data.meses != null ? parseInt(data.meses, 10) : null,
                activo: data.activo,
                orden: parseInt(data.orden, 10) || 0
            };
            if (editing) {
                await periodosServicioAPI.update(editing, payload);
                toast.success('Periodo actualizado');
            } else {
                await periodosServicioAPI.create(payload);
                toast.success(`Periodo "${payload.nombre}" creado`);
            }
            cerrarForm();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActivo = async (p) => {
        try {
            await periodosServicioAPI.update(p.id, { activo: !p.activo });
            cargar();
        } catch (err) {
            toast.error(err.message || 'No pudimos cambiar el estado');
        }
    };

    const handleEliminar = async (p) => {
        const ok = await confirm({
            title: '¿Eliminar periodo?',
            message: <p>Se eliminará <strong>{p.nombre}</strong>. Solo se puede eliminar si ninguna cotización lo usa.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await periodosServicioAPI.delete(p.id);
            toast.success('Periodo eliminado');
            cargar();
        } catch (err) {
            toast.error(err.message || 'No se pudo eliminar');
        }
    };

    if (loading) return <div className="loading">Cargando periodos...</div>;

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Períodos de servicio</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <div>
                        <h3 className="h3">Catálogo de períodos</h3>
                        <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                            Recurrencias que se pueden asignar a los servicios de una cotización (mensual, anual, etc.). Etiqueta informativa — no afecta cálculos.
                        </p>
                    </div>
                    <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={abrirCrear}>
                        Nuevo período
                    </Button>
                </div>

                {periodos.length === 0 ? (
                    <div className="empty-state" style={{ padding: 32, textAlign: 'center', color: 'rgb(var(--muted))' }}>
                        No hay períodos configurados. Crea el primero para empezar.
                    </div>
                ) : (
                <div className="prefijos-list">
                    {periodos.map(p => (
                        <Card key={p.id} className={`prefijo-card ${!p.activo ? 'is-inactive' : ''}`}>
                            <Card.Body className="prefijo-card-body">
                                <div className="prefijo-main">
                                    <div className="prefijo-info">
                                        <div className="prefijo-nombre">
                                            {p.nombre}
                                            {!p.activo && <Badge color="neutral" size="sm">Inactivo</Badge>}
                                            {p.meses != null && (
                                                <Badge color="info" size="sm">
                                                    {p.meses === 0 ? '<1 mes' : p.meses === 1 ? '1 mes' : `${p.meses} meses`}
                                                </Badge>
                                            )}
                                        </div>
                                        {p.descripcion && <div className="prefijo-desc">{p.descripcion}</div>}
                                        <div className="prefijo-meta">Orden: {p.orden}</div>
                                    </div>
                                </div>
                                <div className="prefijo-actions">
                                    <Tooltip content={p.activo ? 'Desactivar' : 'Activar'}>
                                        <Button
                                            variant={p.activo ? 'ghost' : 'soft'}
                                            size="sm"
                                            leftIcon={p.activo ? <X size={13} /> : <Check size={13} />}
                                            onClick={() => handleToggleActivo(p)}
                                        >
                                            {p.activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Editar">
                                        <Button variant="ghost" size="sm" leftIcon={<Pencil size={13} />} onClick={() => abrirEditar(p)}>
                                            Editar
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Eliminar">
                                        <Button variant="ghost" size="sm" leftIcon={<Trash2 size={13} />} onClick={() => handleEliminar(p)} />
                                    </Tooltip>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
                )}

                <Modal
                    open={formModal.open}
                    onClose={cerrarForm}
                    title={formModal.editing ? 'Editar período' : 'Nuevo período'}
                    size="md"
                    footer={
                        <>
                            <Button variant="ghost" size="sm" onClick={cerrarForm}>Cancelar</Button>
                            <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving}>
                                {saving ? 'Guardando...' : (formModal.editing ? 'Guardar cambios' : 'Crear período')}
                            </Button>
                        </>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label className="label">Nombre</label>
                            <Input
                                value={formModal.data.nombre}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, nombre: e.target.value } }))}
                                placeholder="Ej: Mensual, Anual, Trimestral"
                                maxLength={60}
                            />
                        </div>
                        <div>
                            <label className="label">Descripción <span className="text-xs text-muted">(opcional)</span></label>
                            <Input
                                value={formModal.data.descripcion}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, descripcion: e.target.value } }))}
                                placeholder="Para qué se usa este período..."
                                maxLength={255}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label">Meses <span className="text-xs text-muted">(opcional, informativo)</span></label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formModal.data.meses}
                                    onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, meses: e.target.value } }))}
                                    placeholder="12"
                                />
                            </div>
                            <div>
                                <label className="label">Orden</label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formModal.data.orden}
                                    onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, orden: e.target.value } }))}
                                />
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    </>
    );
};

export default PeriodosPanel;
